import { Injectable, inject } from '@angular/core';

import { ISportEvent } from '@frontend/oxygen/shared-utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as _ from 'underscore';

import { EnvironmentConfig, IConfiguration, RacingDataModel } from '../../../client-config';
import { SportsConfigHelperService } from '../../../sb/services/sportsConfig/sport-config-helper.service';
import { RoutingState } from '../../../shared/services/routingState/routing-state.service';
import { IRoutingHelperCompetition, IRoutingHelperEvent } from './routing-helper.model';

@Injectable({ providedIn: 'root' })
export class RoutingHelperService {
    private racingIds: Array<number>;
    private readonly environment: IConfiguration = inject(EnvironmentConfig).configuration;
    // private racingCategories: IConstant = this.environment.CATEGORIES_DATA && this.environment.CATEGORIES_DATA.racing;
    constructor(
        private sportsConfigHelperService: SportsConfigHelperService,
        private routingState: RoutingState,
    ) {
        // this.racingIds = _.map(this.racingCategories, (category) => Number(category.id));
    }

    /**
     * Encodes url part from unneeded symbols.
     * @param {string} part
     * @return {string}
     */
    encodeUrlPart(part: string | number): string {
        return `${part}`
            .replace(/([^a-zA-Z0-9])+/g, '-')
            .replace(/^-+|-+$/g, '') // remove one or more dashes at start of the sting or at end
            .toLowerCase();
    }

    /**
     * Forms URL for sport's event details page based on category, class, type and event names.
     * @param {string|number} options.categoryId
     * @param {string} options.categoryName
     * @param {string} options.className
     * @param {string} options.typeName
     * @param {string} options.name
     * @param {string} options.id
     * @param {string} extensionName
     * @return {string}
     */
    formEdpUrl(options: IRoutingHelperEvent | ISportEvent, isSdmprefixRequired = true): string {
        this.racingIds = _.map(this.environment.CATEGORIES_DATA!.racing!, (category: RacingDataModel) => Number(category.id));
        const isRacing: boolean = _.contains(this.racingIds, Number(options.categoryId));
        const name = options.name || 'name';
        const eventName: string = isRacing ? options.originalName || name : name;
        const parts: Array<string | number> = [options.categoryName, options.className || 'class', options.typeName || 'type', eventName, options.id];
        const url: string = _.map(parts, (part) => `/${this.encodeUrlPart(part)}`).join('');
        const eventPrefix = isRacing ? '' : 'event';
        const sdmPrefix = isRacing ? 'en/sports' : 'en/sports/';
        return !url.includes('en/sports') && isSdmprefixRequired ? `${sdmPrefix}${eventPrefix}${url}` : `${eventPrefix}${url}`;
    }
    /**
     * to form Five A Side URL
     * @param categoryName
     * @param className
     * @param typeName
     * @param name
     * @param id
     * @return {string}
     */
    formFiveASideUrl(categoryName?: string, className?: string, typeName?: string, name?: string, id?: string): string {
        const parts: Array<string | number> = [categoryName || '', className || '', typeName || '', name || '', id || ''];
        const url: string = parts.map((part: string | number) => `/${this.encodeUrlPart(part)}`).join('');
        return `${url}`;
    }

    /**
     * Forms URL for sport or racing page based on sport name and optional tab/sub-tab.
     * @param {string} sportName
     * @param {string=} location
     * @return {string}
     */
    formSportUrl(sportName: string, location?: string): Observable<string> {
        return this.sportsConfigHelperService.getSportPathByName(sportName).pipe(
            map((sportPath: string) => {
                return `/${sportPath || sportName}${location ? `/${location}` : ''}`;
            }),
        );
    }

    /**
     * Forms event details page or sport results page based on event's "isResulted" property.
     * @param {Object} eventEntity
     * @param {string} origin
     * @param {boolean} isRacing
     * @return {string}
     */
    formResultedEdpUrl(eventEntity: IRoutingHelperEvent | ISportEvent, origin: string = ''): string {
        return `${this.formEdpUrl(eventEntity)}${origin}`;
    }

    /**
     * Forms URL for sport competition.
     * @param {string} options.sport
     * @param {string} options.className
     * @param {string} options.typeName
     * @return {string}
     */
    formCompetitionUrl(options: IRoutingHelperCompetition): string {
        return `/competitions${_.map([options.sport, options.className, options.typeName], (part) => `/${this.encodeUrlPart(part)}`).join('')}`;
    }

    formInplayUrl(sportName: string) {
        return `/in-play/${sportName}`;
    }

    formSportCompetitionsUrl(sportURI: string) {
        return `/${sportURI}/competitions`;
    }

    getLastUriSegment(uriPath: string): string {
        const validatedPath: string = /[^\/]$/.test(uriPath) ? uriPath : uriPath.replace(/[^\/]$/, '');
        const lastSegment: RegExpMatchArray | null = validatedPath.match('[^/]+(?=/$|$)');
        return lastSegment ? lastSegment[0] : uriPath;
    }

    getPreviousSegment(): string {
        return this.routingState.getPreviousSegment();
    }

    getPreviousUrl(): string {
        return this.routingState.getPreviousUrl();
    }

    // formSDMURL(url: string): string {
    //     const sdmPrefix = url.startsWith('/') ? 'en/sports' : 'en/sports/';
    //     return this.productService.isSingleDomainApp && !url?.includes('http') && !url.includes('en/sports') ? `${sdmPrefix}${url}` : url;
    // }
}
